import { createTheme, PaletteColor, Theme, ThemeProvider } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Brand, ReEvent } from "../interfaces/types";

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: PaletteColor;
    warning: PaletteColor;
    links: PaletteColor;
    clickedLinks: PaletteColor;
    dragBorderColor: PaletteColor;
    navbarColor: PaletteColor;
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
    warning?: PaletteOptions["primary"];
    links?: PaletteOptions["primary"];
    clickedLinks?: PaletteOptions["primary"];
    dragBorderColor?: PaletteOptions["primary"];
    navbarColor?: PaletteOptions["primary"];
  }
}

export type CustomThemeContextType = {
  brand: Brand;
  theme: Theme;
  updateTheme: (event?: ReEvent) => void;
};

const defaultTheme = createTheme({
  palette: {
    primary: { main: "#51AE32" },
    secondary: { main: "#63ADF7" },
    tertiary: { main: "#F2C52B" },
    error: { main: "#FF0000" },
    warning: { main: "#FFA500" },
    info: { main: "#0000FF" },
    links: { main: "#0000FF" },
    clickedLinks: { main: "#FF0000" },

    //following colors are not depending on the event
    dragBorderColor: { main: blue[300] }, //"#6c0404"
    navbarColor: { main: grey[400] },
  },
});

const defaultBrand: Brand = {
  name: "Re-evented",
  logoUrl:
    "https://firebasestorage.googleapis.com/v0/b/stage-propile.appspot.com/o/logos%2FRe-evented.webp?alt=media&token=4b6d2561-2852-4cba-914a-823c2e34f0a7",
  faviconUrl:
    "https://firebasestorage.googleapis.com/v0/b/stage-propile.appspot.com/o/favicons%2FRe-evented.ico?alt=media&token=d46c4dc8-b62a-45a7-9583-f1f5dbcc13ef",
};

const CustomThemeContext = createContext<CustomThemeContextType>({
  brand: defaultBrand,
  theme: defaultTheme,
  updateTheme: () => {},
});

const updateFavicon = (faviconUrl: string) => {
  const link: HTMLLinkElement | null =
    document.querySelector("link[rel*='icon']");
  if (link) {
    link.href = faviconUrl;
  } else {
    const newLink = document.createElement("link");
    newLink.rel = "icon";
    newLink.href = faviconUrl;
    document.head.appendChild(newLink);
  }
};

export const CustomThemeProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [brand, setBrand] = useState<Brand>(defaultBrand);

  useEffect(() => {
    setTheme(defaultTheme);
    setBrand(defaultBrand);
  }, []);

  useEffect(() => {
    updateFavicon(brand.faviconUrl);
  }, [brand]);

  const updateTheme = (event?: ReEvent) => {
    if (!event) {
      setTheme(defaultTheme);
      setBrand(defaultBrand);
      return;
    }
    if (event.eventConfiguration.hasCustomStyling) {
      const newTheme = createTheme({
        ...defaultTheme,
        palette: {
          primary: { main: event.eventConfiguration.primaryColor },
          secondary: { main: event.eventConfiguration.secondaryColor },
          tertiary: { main: event.eventConfiguration.tertiaryColor },
          error: { main: event.eventConfiguration.errorColor },
          warning: { main: event.eventConfiguration.warningColor },
          info: { main: event.eventConfiguration.infoColor },
          links: { main: event.eventConfiguration.linkColor },
          clickedLinks: { main: event.eventConfiguration.clickedLinkColor },
          //following colors are not depending on the event
          dragBorderColor: { main: blue[300] }, //"#6c0404"
          navbarColor: { main: grey[400] },
        },
      });
      setTheme(newTheme);
    } else {
      setTheme(defaultTheme);
    }

    const newBrand: Brand = {
      name: event.name || defaultBrand.name,
      logoUrl: event.eventConfiguration.logoUrl || defaultBrand.logoUrl,
      faviconUrl:
        event.eventConfiguration.faviconUrl || defaultBrand.faviconUrl,
    };
    setBrand(newBrand);
  };

  const value: CustomThemeContextType = { brand, theme, updateTheme };
  return (
    <CustomThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CustomThemeContext.Provider>
  );
};

export const useCustomThemeContext = (): CustomThemeContextType => {
  return useContext(CustomThemeContext);
};
